import React from 'react';
import { Form, Badge, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faBell, faCreditCard, faPen } from '@fortawesome/free-solid-svg-icons';
import {
  SettingsCard,
  CardHeader,
  CardTitle,
  CardTitleText,
  PreferenceRow,
  PreferenceInfo,
  PreferenceLabel,
  PreferenceDescription,
  FormSection,
  SaveChangesButton,
  CancelButton,
  ActionButtons,
  StyledSwitch,
  ActionIconButton,
} from './Styles';
import { useTheme } from '../../context/ThemeContext';
import type { PreferencesSectionProps } from './types';

const PreferencesSection: React.FC<PreferencesSectionProps> = ({
  editingSection,
  formData,
  loading,
  profileLoading,
  onEdit,
  onSwitchChange,
  onSave,
  onCancel,
}) => {
  const { isDarkMode } = useTheme();
  
  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <FontAwesomeIcon icon={faCog} style={{ fontSize: 20, color: '#1890ff' }} />
          <CardTitleText level={4}>Preferences</CardTitleText>
        </CardTitle>
        <ActionIconButton
          onClick={profileLoading ? undefined : onEdit}
          iconColor="#2D6CCA"
          style={{ opacity: profileLoading ? 0.5 : 1, cursor: profileLoading ? 'not-allowed' : 'pointer' }}
        >
          <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
        </ActionIconButton>
      </CardHeader>
      {editingSection !== 'additional' ? (
        <div>
          <PreferenceRow>
            <FontAwesomeIcon icon={faBell} style={{ fontSize: 16, color: '#1890ff' }} />
            <PreferenceInfo>
              <PreferenceLabel>Receive Notifications</PreferenceLabel>
              <PreferenceDescription>Get notified about important updates</PreferenceDescription>
            </PreferenceInfo>
            <Badge
              status={formData.receiveNotifications ? 'success' : 'default'}
              text={formData.receiveNotifications ? 'Enabled' : 'Disabled'}
            />
          </PreferenceRow>
          <PreferenceRow>
            <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: 16, color: '#1890ff' }} />
            <PreferenceInfo>
              <PreferenceLabel>Auto-Renew Subscription</PreferenceLabel>
              <PreferenceDescription>Automatically renew your subscription</PreferenceDescription>
            </PreferenceInfo>
            <Badge
              status={formData.autoRenewSubscription ? 'success' : 'default'}
              text={formData.autoRenewSubscription ? 'Enabled' : 'Disabled'}
            />
          </PreferenceRow>
        </div>
      ) : (
        <FormSection>
          <Form.Item label="Receive Notifications">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <FontAwesomeIcon icon={faBell} style={{ fontSize: 16, color: '#1890ff' }} />
              <div style={{ flex: 1 }}>
                <Typography.Text strong>Receive Notifications</Typography.Text>
                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  Get notified about important updates
                </Typography.Text>
              </div>
              <StyledSwitch
                checked={formData.receiveNotifications}
                onChange={(checked) => onSwitchChange('receiveNotifications', checked)}
              />
            </div>
          </Form.Item>
          <Form.Item label="Auto-Renew Subscription">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: 16, color: '#1890ff' }} />
              <div style={{ flex: 1 }}>
                <Typography.Text strong>Auto-Renew Subscription</Typography.Text>
                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  Automatically renew your subscription
                </Typography.Text>
              </div>
              <StyledSwitch
                checked={formData.autoRenewSubscription}
                onChange={(checked) => onSwitchChange('autoRenewSubscription', checked)}
              />
            </div>
          </Form.Item>
          <ActionButtons>
            <SaveChangesButton icon={<CheckOutlined />} onClick={onSave} loading={loading}>
              Save Changes
            </SaveChangesButton>
            <CancelButton onClick={onCancel}>
              Cancel
            </CancelButton>
          </ActionButtons>
        </FormSection>
      )}
    </SettingsCard>
  );
};

export default React.memo(PreferencesSection);

